// Güncellemeler (duyurular) ekranının saf yardımcıları: API kaydını normalize eder,
// "bu hafta" hesabını yapar ve kayıtları gün -> ürün bölümü -> madde olarak gruplar.
// Sunucu tarihleri bilerek new Date(str) ile okunmaz ("YYYY-MM-DD HH:mm:ss" bazı
// tarayıcılarda Invalid Date, "GG/AA/YYYY" ise AA/GG okunur); karşılaştırmalar
// "YYYY-MM-DD" string'i üzerinden yapılır.

export interface UrunRef {
  value: number;
  text: string;
}

export interface UpdateItem {
  id: string;
  title: string;
  content: string;      // HTML destekli kısa açıklama
  gun: string;          // duyuru_tarih günü, "YYYY-MM-DD"
  girisGunu: string;    // created_at günü (sisteme giriş); yoksa gun
  tags: string[];
  products: UrunRef[];  // tekil, value'ya göre artan
}

export interface UrunBolumu {
  key: string;          // ürün value'ları, ör. "2,3"
  products: UrunRef[];
  items: UpdateItem[];
}

export interface GunGrubu {
  gun: string;
  baslik: string;
  yeni: boolean;
  adet: number;
  bolumler: UrunBolumu[];
}

const GUN_REGEX = /^(\d{4})-(\d{2})-(\d{2})/;
const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const GUNLER = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

function pad(n: number): string {
  return n < 10 ? "0" + n : String(n);
}

/** Yerel saate göre "YYYY-MM-DD" (toISOString UTC'ye çevirdiği için kullanılmaz) */
export function gunAnahtari(d: Date): string {
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

function gunOku(val: any): string | null {
  const m = GUN_REGEX.exec(val == null ? "" : String(val));
  return m ? m[1] + "-" + m[2] + "-" + m[3] : null;
}

function jsonListe(val: any): any[] {
  if (Array.isArray(val)) return val;
  if (typeof val !== "string" || !val) return [];
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

/** İçinde bulunulan takvim haftasının Pazartesi günü, "YYYY-MM-DD" */
export function haftaBaslangici(simdi: Date): string {
  const geri = (simdi.getDay() + 6) % 7;
  return gunAnahtari(new Date(simdi.getFullYear(), simdi.getMonth(), simdi.getDate() - geri));
}

function buHaftada(gun: string, simdi: Date): boolean {
  return gun >= haftaBaslangici(simdi) && gun <= gunAnahtari(simdi);
}

/** Bu hafta (Pazartesi 00:00'dan bugüne) yayınlanan ya da sisteme girilen duyuru yenidir */
export function yeniMi(item: UpdateItem, simdi: Date): boolean {
  return buHaftada(item.girisGunu, simdi) || buHaftada(item.gun, simdi);
}

/** Badge sayısı: bu haftaki farklı duyuru adedi */
export function yeniSayisi(items: UpdateItem[], simdi: Date): number {
  const sayilan: { [id: string]: boolean } = {};
  let sayi = 0;
  items.forEach(it => {
    if (!sayilan[it.id] && yeniMi(it, simdi)) {
      sayilan[it.id] = true;
      sayi++;
    }
  });
  return sayi;
}

export function normalizeDuyuru(raw: any): UpdateItem | null {
  if (!raw) return null;
  const gun = gunOku(raw.duyuru_tarih);
  if (!gun) return null;

  // Bozuk bir eleman sadece kendisini atlatır, kaydın diğer ürünlerini değil
  const products: UrunRef[] = [];
  jsonListe(raw.product).forEach(p => {
    const value = p && p.id ? Number(p.id.value) : NaN;
    if (isNaN(value) || products.some(x => x.value === value)) return;
    products.push({ value: value, text: String(p.id.text || "") });
  });
  products.sort((a, b) => a.value - b.value);

  const tags: string[] = [];
  jsonListe(raw.duyuru_type).forEach(t => {
    const text = t && t.id ? String(t.id.text || "") : "";
    if (text && tags.indexOf(text) === -1) tags.push(text);
  });

  return {
    id: String(raw.duyuru_id),
    title: String(raw.duyuru_baslik || "").trim(),
    content: String(raw.duyuru_metin || "").trim(),
    gun: gun,
    girisGunu: gunOku(raw.created_at) || gun,
    tags: tags,
    products: products
  };
}

/** API yanıtını UpdateItem listesine çevirir; tarihi okunamayan kayıtlar atlanır */
export function duyurulariNormalizeEt(data: any): UpdateItem[] {
  const sonuc: UpdateItem[] = [];
  (Array.isArray(data) ? data : []).forEach(raw => {
    const item = normalizeDuyuru(raw);
    if (item) sonuc.push(item);
  });
  return sonuc;
}

/** "2026-09-04" -> "4 Eylül 2026 Cuma" */
export function gunBasligi(gun: string): string {
  const m = GUN_REGEX.exec(gun);
  if (!m) return gun;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getDate() + " " + AYLAR[d.getMonth()] + " " + d.getFullYear() + " " + GUNLER[d.getDay()];
}

// Çok ürünlü bölümler üstte; eşitlikte ürün value'larına göre
function bolumSirasi(a: UrunBolumu, b: UrunBolumu): number {
  if (a.products.length !== b.products.length) return b.products.length - a.products.length;
  for (let i = 0; i < a.products.length; i++) {
    if (a.products[i].value !== b.products[i].value) return a.products[i].value - b.products[i].value;
  }
  return 0;
}

/**
 * Aynı günün duyurularını tek grupta toplar (son gün en üstte). Gün içinde her
 * ürün kombinasyonu ayrı bölümdür; çok ürünlü duyuru tek bölümde, bir kez görünür.
 */
export function gunlereGrupla(items: UpdateItem[], simdi: Date): GunGrubu[] {
  const gunler: { [gun: string]: UpdateItem[] } = {};
  const goruldu: { [id: string]: boolean } = {};
  items.forEach(it => {
    if (goruldu[it.id]) return;
    goruldu[it.id] = true;
    (gunler[it.gun] = gunler[it.gun] || []).push(it);
  });

  return Object.keys(gunler).sort().reverse().map(gun => {
    const liste = gunler[gun].slice().sort((a, b) => Number(b.id) - Number(a.id));
    const bolumMap: { [key: string]: UrunBolumu } = {};
    const bolumler: UrunBolumu[] = [];
    liste.forEach(it => {
      const key = it.products.map(p => p.value).join(",");
      if (!bolumMap[key]) {
        bolumMap[key] = { key: key, products: it.products, items: [] };
        bolumler.push(bolumMap[key]);
      }
      bolumMap[key].items.push(it);
    });
    bolumler.sort(bolumSirasi);

    return {
      gun: gun,
      baslik: gunBasligi(gun),
      yeni: liste.some(it => yeniMi(it, simdi)),
      adet: liste.length,
      bolumler: bolumler
    };
  });
}
